import { useState, ChangeEvent, ClipboardEvent } from 'react'
import styled from 'styled-components'

const numberRegex = /^[0-9]*$/

interface Props {
  digits?: number
  onChange?: (value: string) => void
}

const PasscodeInput = ({ digits = 6, onChange }: Props) => {
  const [values, setValues] = useState<string[]>(Array(digits).fill(''))

  const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    if (!numberRegex.test(e.target.value)) return

    const replaced = e.target.value.length > 1 ? e.target.value.replace(values[index], '') : e.target.value

    const list = [...values]
    list[index] = replaced
    setValues(list)
    onChange?.(list.join(''))
    focusShift(index)
  }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const data = e.clipboardData.getData('text')
    const list = [...values]

    const longerLength = data.length > digits ? data.length : digits
    let currValueIndex = 0

    for (let i = 0; i < longerLength; i++) {
      if (currValueIndex < list.length && numberRegex.test(data[i])) {
        list[currValueIndex] = data[i] || list[currValueIndex]
        currValueIndex++
      }
    }

    setValues(list)
    onChange?.(list.join(''))
  }

  return (
    <Container onPasteCapture={handlePaste}>
      {values.map((i, idx) => (
        <Cell key={`passcode_input_${idx}`}>
          <input tabIndex={idx} autoComplete="none" onChange={handleChange(idx)} value={i} />
        </Cell>
      ))}
    </Container>
  )
}

const focusShift = (currentIndex: number) => {
  const nextInput = document.querySelector(`input[tabindex="${currentIndex + 1}"]`) as HTMLInputElement | null
  if (nextInput) {
    nextInput.focus()
  }
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
`

const Cell = styled.div`
  ${(p) => `
  --bg: ${p.theme.colors.white};
  --border: ${p.theme.colors.defaultBorder};
  --border-focus: ${p.theme.colors.blue2};
  `}

  height: 60px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--bg);
  border: 1px solid var(--border);

  &:is(:first-child) {
    border-radius: 4px 0 0 4px;
  }

  &:is(:last-child) {
    border-radius: 0 4px 4px 0;
  }

  &:not(:last-child) {
    border-right: none;
  }

  &:focus-within {
    border: 1px solid var(--border-focus);
  }

  input {
    font-size: 22px;
    width: 100%;
    border: none;
    text-align: center;
  }
`

export default PasscodeInput
